import React from 'react';
import PropTypes from 'prop-types';
import { CountryFlag, Grid, Stack, Typography } from '@esgian/esgianui';
import { ProfileLink } from '@components/Links';
import { formatNumberReadable } from '@helpers';
import { useSegment } from '@hooks';
import { useSelector } from 'react-redux';
import { getCommercialCategoryType, getOperatorType } from '@store/features';
import { TextWithTooltipIcon } from '@components';
import {
  IN_ASSOCIATION_WITH_HESNES_SHIPPING_AS_TOOLTIP,
  OPERATOR_AVG_AGE_TITLE,
  OPERATOR_AVG_CAP_MPP_TITLE,
  OPERATOR_AVG_CAP_RORO_TITLE,
  OPERATOR_FLEET_SIZE_TITLE,
  OPERATOR_TOTAL_CAP_MPP_TITLE,
  OPERATOR_TOTAL_CAP_RORO_TITLE
} from '@constants/tooltips';
import TableV2 from '@components/Tables/TableV2';

function OperatorsTable({ operators, loading, title, height, fileName }) {
  const { isRoRo } = useSegment();
  const operatorType = useSelector(getOperatorType);
  const commercialCategoryType = useSelector(getCommercialCategoryType);

  const columns = [
    {
      name: 'operatorLongName',
      label: 'Operator Name',
      options: {
        sort: true,
        sortThirdClickReset: true,
        customFilterType: 'multiselect',
        setCellProps: () => {
          return {
            style: {
              boxShadow: '3px 0px 2px -1px rgb(0 0 0 / 20%)'
            }
          };
        },
        customBodyRender: (dataIndex, rowIndex) => {
          const { rowData } = rowIndex;
          return dataIndex !== null ? (
            <ProfileLink profile={'operator'} id={rowData[1]} name={dataIndex} />
          ) : (
            '--'
          );
        }
      }
    },
    {
      name: 'operatorId',
      options: {
        display: false,
        viewColumns: false,
        filter: false,
        download: false
      }
    },
    {
      name: 'operatorShortName',
      label: 'Short Name',
      options: {
        customFilterType: 'multiselect',
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: 'countryId',
      options: { display: false, viewColumns: false, filter: false, download: false }
    },
    {
      name: 'countryCode',
      options: { display: false, viewColumns: false, filter: false, download: false }
    },
    {
      name: 'countryName',
      label: 'Country',
      options: {
        customFilterType: 'multiselect',
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (dataIndex, rowIndex) => {
          const { rowData } = rowIndex;
          return dataIndex !== null ? (
            <Grid container spacing={1}>
              <Grid item sx={{ placeSelf: 'flex-end' }}>
                <CountryFlag size={20} countryCode={rowData[4]} />
              </Grid>
              <Grid item>
                <Typography variant={'caption'}>{dataIndex}</Typography>
              </Grid>
            </Grid>
          ) : (
            '--'
          );
        }
      }
    },
    {
      name: 'fleetSize',
      label: 'Fleet Size',
      options: {
        customHeadLabelRender: () => (
          <TextWithTooltipIcon
            label={'Fleet Size'}
            tooltipText={OPERATOR_FLEET_SIZE_TITLE}
            labelVariant={'body2'}
          />
        ),
        sort: true,
        sortThirdClickReset: true,
        customFilterType: 'minMax'
      }
    },
    {
      name: 'avgAge',
      label: 'Avg. Age',
      options: {
        customHeadLabelRender: () => (
          <TextWithTooltipIcon
            label={'Avg. Age'}
            tooltipText={OPERATOR_AVG_AGE_TITLE}
            labelVariant={'body2'}
          />
        ),
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (value) => {
          if (!Number.isFinite(value)) return '-';
          return parseFloat(value?.toFixed(2));
        },
        customFilterType: 'minMax'
      }
    },
    {
      name: 'avgCapacity',
      label: isRoRo ? 'Avg. Capacity CEU' : 'Avg. Capacity DWT',
      options: {
        customHeadLabelRender: () => {
          let label = 'Avg. Capacity CEU';
          let tooltip = OPERATOR_AVG_CAP_RORO_TITLE;
          if (!isRoRo) {
            label = 'Avg. Capacity DWT';
            tooltip = OPERATOR_AVG_CAP_MPP_TITLE;
          }
          return <TextWithTooltipIcon label={label} tooltipText={tooltip} labelVariant={'body2'} />;
        },
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (value) => {
          if (!Number.isFinite(value)) return '-';
          return formatNumberReadable(value.toFixed(2));
        },
        customFilterType: 'minMax'
      }
    },
    {
      name: 'totalCapacity',
      label: isRoRo ? 'Total Capacity CEU' : 'Total Capacity DWT',
      options: {
        customHeadLabelRender: () => {
          let label = 'Total Capacity CEU';
          let tooltip = OPERATOR_TOTAL_CAP_RORO_TITLE;
          if (!isRoRo) {
            label = 'Total Capacity DWT';
            tooltip = OPERATOR_TOTAL_CAP_MPP_TITLE;
          }
          return <TextWithTooltipIcon label={label} tooltipText={tooltip} labelVariant={'body2'} />;
        },
        sort: true,
        sortThirdClickReset: true,
        customFilterType: 'minMax',
        customBodyRender: (value) => {
          if (!Number.isFinite(value)) return '-';
          return formatNumberReadable(value);
        }
      }
    }
  ];

  const options = {
    selectableRows: 'none',
    responsive: 'standard',
    filter: false,
    search: false,
    download: false,
    tableBodyMaxHeight: height,
    filename: fileName,
    setTableProps: () => {
      return {
        id: 'operators-table'
      };
    },
    print: false,
    elevation: 0,
    customStyle: {
      MuiTableBody: {
        styleOverrides: {
          root: {
            'tr:nth-of-type(even) td': {
              backgroundColor: '#212f41'
            },
            'tr:nth-of-type(odd) td': {
              backgroundColor: '#fff'
            }
          }
        }
      },
      MUIDataTableFilterList: {
        styleOverrides: {
          root: {
            margin: '16px'
          }
        }
      }
    },
    viewColumns: false,
    pagination: true,
    rowsPerPageOptions: [10, 25, 50, 100],
    sortOrder: {
      name: 'fleetSize',
      direction: 'desc'
    }
  };

  let updateText = '';
  if (commercialCategoryType === 1) {
    updateText = ', last updated April, 2025';
  } else if (commercialCategoryType === 2) {
    updateText = ', last updated May, 2025';
  } else if (commercialCategoryType === 3) {
    updateText = ', last updated April, 2025';
  } else if (commercialCategoryType === 4) {
    updateText = ', last updated May, 2025';
  } else {
    updateText = ', last updated May, 2025';
  }

  return (
    <TableV2
      data={operators}
      options={options}
      columns={columns}
      loading={loading}
      title={
        <Stack>
          <Typography id={'operators-title'} variant={'h6'}>
            {title}
          </Typography>
          {isRoRo ? (
            <TextWithTooltipIcon
              label={
                `In association with Hesnes Shipping AS` +
                (operatorType === 1 ? `, last updated May, 2025` : '') +
                (operatorType === 2 ? `, last updated May, 2025` : '')
              }
              tooltipText={
                <Typography>{IN_ASSOCIATION_WITH_HESNES_SHIPPING_AS_TOOLTIP}</Typography>
              }
              color={'inherit'}
              labelVariant={'body2'}
              labelBold={false}
              tooltipMaxWidth="600px"
            />
          ) : (
            <Typography variant={'body2'}>
              {`In association with Toepfer Transport GmbH${updateText}`}
            </Typography>
          )}
        </Stack>
      }
    />
  );
}

OperatorsTable.propTypes = {
  operators: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  title: PropTypes.string,
  height: PropTypes.string,
  fileName: PropTypes.string.isRequired
};

OperatorsTable.defaultProps = {
  operators: [],
  loading: false,
  title: 'Active Operators',
  height: '80vh'
};

export default OperatorsTable;
